import type { ConditionState, LayerId, OntoPathNode, PathGenerator } from '../types'
import type {
  Locale,
  PathLabelsCompliance,
  PathLabelsCost,
  PathLabelsUptime,
} from '../lib/locales'

export const LAYER_IDS: readonly LayerId[] = ['L1', 'L2', 'L3'] as const

export const BASELINE_CONDITIONS: ConditionState = {
  H2S_pct: 12.6,
  Cl_mg_L: 18300,
  vapor_pressure_ratio: 0.42,
}

export const DEFAULT_GOAL_ID = 'compliance'

const ACIDIC_THRESHOLD_PCT = 0.005   // 50 ppm
const HIGH_CL_THRESHOLD = 10000      // mg/L
const HIGH_VP_THRESHOLD = 0.3        // ratio

function formatPpm(pct: number): string {
  if (pct >= 1) return `${pct.toFixed(1)}%`
  return `${Math.round(pct * 10000)} ppm`
}

function format(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars[key]
    return v === undefined || v === null ? '' : String(v)
  })
}

function buildComplianceGenerator(l: PathLabelsCompliance): PathGenerator {
  return (c: ConditionState): OntoPathNode[] => {
    const acidic = c.H2S_pct >= ACIDIC_THRESHOLD_PCT
    const highCl = c.Cl_mg_L > HIGH_CL_THRESHOLD
    const highVP = c.vapor_pressure_ratio > HIGH_VP_THRESHOLD
    const matRisk = acidic && highCl
    const planRisk = highVP
    const anyConstraint = matRisk || planRisk

    const nodes: OntoPathNode[] = [
      { id: 'g1-goal', kind: 'goal', label: l.goalLabel, detail: l.goalDetail },
      {
        id: 'g1-obj',
        kind: 'object',
        label: l.objLabel,
        detail: l.objDetail,
        parents: ['g1-goal'],
      },
      {
        id: 'g1-inf-cond',
        kind: 'inference',
        label: l.infCondLabel,
        detail: format(l.infCondDetailTpl, {
          h2s: formatPpm(c.H2S_pct),
          cl: c.Cl_mg_L.toLocaleString(),
          vp: c.vapor_pressure_ratio.toFixed(2),
        }),
        parents: ['g1-obj'],
      },
      {
        id: 'g1-rfq',
        kind: 'object',
        label: l.rfqLabel,
        detail: l.rfqDetail,
        parents: ['g1-goal'],
      },
    ]

    if (matRisk) {
      nodes.push({
        id: 'g1-c-mat',
        kind: 'constraint',
        label: l.cMatLabel,
        detail: l.cMatDetail,
        citation: l.cMatCitation,
        parents: ['g1-inf-cond'],
      })
    }
    if (planRisk) {
      nodes.push({
        id: 'g1-c-plan',
        kind: 'constraint',
        label: l.cPlanLabel,
        detail: format(l.cPlanDetailTpl, { vp: c.vapor_pressure_ratio.toFixed(2) }),
        citation: l.cPlanCitation,
        parents: ['g1-inf-cond'],
      })
    }
    if (!anyConstraint) {
      nodes.push({
        id: 'g1-c-none',
        kind: 'inference',
        label: l.cNoneLabel,
        detail: l.cNoneDetail,
        parents: ['g1-inf-cond'],
      })
    }

    const candidateParents: string[] = []
    if (matRisk) candidateParents.push('g1-c-mat')
    if (planRisk) candidateParents.push('g1-c-plan')
    if (!anyConstraint) candidateParents.push('g1-c-none')
    candidateParents.push('g1-rfq')

    const candidateDetail = !anyConstraint
      ? l.candDetailOk
      : [
          matRisk ? l.candViolPieceMat : null,
          planRisk ? l.candViolPiecePlan : null,
          l.candViolPieceCert,
        ]
          .filter(Boolean)
          .join(' · ')

    nodes.push({
      id: 'g1-cand',
      kind: 'candidate',
      label: anyConstraint ? l.candLabelViol : l.candLabelOk,
      detail: candidateDetail,
      parents: candidateParents,
    })

    let lastNode = 'g1-cand'
    if (anyConstraint) {
      nodes.push({
        id: 'g1-inf-cov',
        kind: 'inference',
        label: l.infCovLabel,
        detail: l.infCovDetail,
        parents: ['g1-cand'],
      })
      lastNode = 'g1-inf-cov'
    }

    nodes.push({
      id: 'g1-act',
      kind: 'action',
      label: anyConstraint ? l.actLabelViol : l.actLabelOk,
      detail: anyConstraint ? l.actDetailViol : l.actDetailOk,
      parents: [lastNode],
    })

    return nodes
  }
}

function buildUptimeGenerator(l: PathLabelsUptime): PathGenerator {
  return (c: ConditionState): OntoPathNode[] => {
    const corrosionFactor =
      c.H2S_pct >= ACIDIC_THRESHOLD_PCT ? 1 + Math.min(c.H2S_pct / 20, 0.5) : 1
    const remainingDaysLo = Math.max(3, Math.round(11 / corrosionFactor))
    const remainingDaysHi = Math.max(remainingDaysLo + 2, Math.round(14 / corrosionFactor))

    return [
      { id: 'g2-goal', kind: 'goal', label: l.goalLabel, detail: l.goalDetail },
      {
        id: 'g2-obj',
        kind: 'object',
        label: l.objLabel,
        detail: l.objDetail,
        parents: ['g2-goal'],
      },
      {
        id: 'g2-pi',
        kind: 'object',
        label: l.piLabel,
        detail: l.piDetail,
        parents: ['g2-goal'],
      },
      {
        id: 'g2-inf-life',
        kind: 'inference',
        label: l.infLifeLabel,
        detail: format(l.infLifeDetailTpl, {
          h2s: formatPpm(c.H2S_pct),
          factor: corrosionFactor.toFixed(2),
          lo: remainingDaysLo,
          hi: remainingDaysHi,
        }),
        parents: ['g2-obj', 'g2-pi'],
      },
      {
        id: 'g2-c-sla',
        kind: 'constraint',
        label: l.cSlaLabel,
        detail: l.cSlaDetail,
        citation: l.cSlaCitation,
        parents: ['g2-inf-life'],
      },
      {
        id: 'g2-cand',
        kind: 'candidate',
        label: l.candLabel,
        detail: l.candDetail,
        parents: ['g2-c-sla'],
      },
      {
        id: 'g2-act',
        kind: 'action',
        label: l.actLabel,
        detail: l.actDetail,
        parents: ['g2-cand'],
      },
    ]
  }
}

function buildCostGenerator(l: PathLabelsCost): PathGenerator {
  return (c: ConditionState): OntoPathNode[] => {
    const acidic = c.H2S_pct >= ACIDIC_THRESHOLD_PCT
    const highCl = c.Cl_mg_L > HIGH_CL_THRESHOLD
    const highVP = c.vapor_pressure_ratio > HIGH_VP_THRESHOLD
    const aggressive = (acidic ? 1 : 0) + (highCl ? 1 : 0) + (highVP ? 1 : 0)

    const aCost = (18.2 + (highVP ? 44.2 : 6.0)).toFixed(1)
    const bCost = (35.6 + (acidic && highCl ? 35.4 : 4.0)).toFixed(1)
    const dCost = (25.0 + 22.8 - aggressive * 3.5).toFixed(1)

    const bestId = aggressive > 0 ? 'D' : 'A'
    const bestCost = bestId === 'D' ? dCost : aCost

    return [
      { id: 'g3-goal', kind: 'goal', label: l.goalLabel, detail: l.goalDetail },
      {
        id: 'g3-obj-cost',
        kind: 'object',
        label: l.costModelLabel,
        detail: l.costModelDetail,
        parents: ['g3-goal'],
      },
      {
        id: 'g3-obj-risk',
        kind: 'object',
        label: l.riskModelLabel,
        detail: l.riskModelDetail,
        parents: ['g3-goal'],
      },
      {
        id: 'g3-inf-mc',
        kind: 'inference',
        label: l.infMcLabel,
        detail: format(l.infMcDetailTpl, {
          h2s: formatPpm(c.H2S_pct),
          clk: (c.Cl_mg_L / 1000).toFixed(1),
          vp: c.vapor_pressure_ratio.toFixed(2),
          a: aCost,
          b: bCost,
          d: dCost,
        }),
        parents: ['g3-obj-cost', 'g3-obj-risk'],
      },
      {
        id: 'g3-c-moc',
        kind: 'constraint',
        label: l.cMocLabel,
        detail: aggressive > 0 ? l.cMocDetailNeeded : l.cMocDetailNotNeeded,
        citation: l.cMocCitation,
        parents: ['g3-inf-mc'],
      },
      {
        id: 'g3-cand',
        kind: 'candidate',
        label: l.candLabel,
        detail: format(l.candDetailTpl, { best: bestId, cost: bestCost }),
        parents: ['g3-c-moc'],
      },
      {
        id: 'g3-act',
        kind: 'action',
        label: aggressive > 0 ? l.actLabelAggressive : l.actLabelMild,
        detail:
          aggressive > 0
            ? format(l.actDetailAggressiveTpl, { d: dCost })
            : format(l.actDetailMildTpl, { a: aCost }),
        parents: ['g3-cand'],
      },
    ]
  }
}

export function getPathGenerators(t: Locale): Record<string, PathGenerator> {
  return {
    compliance: buildComplianceGenerator(t.l3.paths.compliance),
    uptime: buildUptimeGenerator(t.l3.paths.uptime),
    cost: buildCostGenerator(t.l3.paths.cost),
  }
}
